import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideSmartphone, lucideX } from '@ng-icons/lucide';

import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-install-prompt',
  standalone: true,
  imports: [NgIcon],
  providers: [provideIcons({ lucideSmartphone, lucideX })],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (showPrompt) {
      <!-- Mirrors the environment banner above it: one colour block, colours
           kept together so nothing can drop out of the class list. -->
      <div class="relative z-50 flex items-center gap-3 bg-indigo-600 px-4 py-2 text-sm text-white">
        <button
          type="button"
          class="flex items-center gap-2 rounded-md bg-white/15 px-3 py-1 font-medium transition-colors hover:bg-white/30"
          (click)="installPWA()">
          <ng-icon name="lucideSmartphone" />
          App installieren
        </button>

        <div class="ms-auto flex shrink-0 items-center gap-1">
          <button
            type="button"
            class="rounded-md px-2 py-1 text-xs text-white/80 underline underline-offset-2 transition-colors hover:bg-white/15 hover:text-white hover:no-underline"
            (click)="dismissForTwoMonths()">
            Nicht mehr anzeigen
          </button>
          <button
            type="button"
            aria-label="Hinweis schließen"
            title="Bis morgen ausblenden"
            class="rounded-full p-1 transition-colors hover:bg-white/20"
            (click)="dismiss()">
            <ng-icon name="lucideX" />
          </button>
        </div>
      </div>
    }
  `,
})
export class InstallPromptComponent implements OnInit, OnDestroy {
  private deferredPrompt: any;
  // ISO timestamp until which the banner stays hidden (localStorage)
  private readonly dismissedKey = 'install-prompt-dismissed-until';
  private readonly dayInMs = 24 * 60 * 60 * 1000;
  // ~2 months, since Date months vary in length
  private readonly twoMonthsInMs = 61 * this.dayInMs;

  showPrompt = false;

  constructor(private logger: LoggerService) {}

  ngOnInit() {
    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
    // Hide the prompt if the PWA is installed
    window.addEventListener('appinstalled', this.handleAppInstalled);
  }

  async installPWA() {
    if (!this.deferredPrompt) return;

    // Show the install prompt
    this.deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await this.deferredPrompt.userChoice;
    this.logger.log(`User response to the install prompt: ${outcome}`);

    // Clear the deferredPrompt variable
    this.deferredPrompt = null;
    this.showPrompt = false;
  }

  /** X clicked: hide until the next day */
  dismiss(): void {
    this.hideFor(this.dayInMs);
  }

  /** "Nicht mehr anzeigen" clicked: hide for 2 months */
  dismissForTwoMonths(): void {
    this.hideFor(this.twoMonthsInMs);
  }

  ngOnDestroy() {
    // Remove event listeners to prevent memory leaks
    window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
    window.removeEventListener('appinstalled', this.handleAppInstalled);
  }

  private hideFor(durationMs: number): void {
    try {
      localStorage.setItem(this.dismissedKey, new Date(Date.now() + durationMs).toISOString());
    } catch {
      // Storage unavailable (e.g. private mode) - only hide for this session
    }
    this.showPrompt = false;
  }

  private isDismissed(): boolean {
    try {
      const until = localStorage.getItem(this.dismissedKey);
      return until !== null && !Number.isNaN(Date.parse(until)) && Date.parse(until) > Date.now();
    } catch {
      return false;
    }
  }

  private handleBeforeInstallPrompt = (e: Event) => {
    e.preventDefault();
    if (this.isDismissed()) return;
    this.deferredPrompt = e;
    this.showPrompt = true;
  };

  private handleAppInstalled = () => {
    this.showPrompt = false;
    this.deferredPrompt = null;
  };
}
